import React, { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Briefcase, Target, Sparkles, Copy, AlertTriangle, CheckCircle2, MessageSquare } from "lucide-react";

const input =
  "w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-3 font-mono text-sm";

function Section({ title, children, accent = "text-[#0033FF]" }) {
  return (
    <div className="border border-black/10 bg-white p-5">
      <div className={`font-mono text-[10px] uppercase tracking-widest ${accent} mb-3`}>{title}</div>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  if (!items || items.length === 0) return <div className="text-sm text-gray-500">—</div>;
  return (
    <ul className="space-y-2">
      {items.map((x, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed">
          <span className="font-mono text-[10px] mt-1 text-[#0033FF]">{String(i + 1).padStart(2, "0")}</span>
          <span>{x}</span>
        </li>
      ))}
    </ul>
  );
}

function InterviewBrief() {
  const [form, setForm] = useState({ company: "", role: "", interviewer: "" });
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!form.company || !form.role) {
      toast.error("Company and role are required");
      return;
    }
    setLoading(true);
    setBrief(null);
    try {
      const r = await api.post("/ai/interview-brief", form);
      setBrief(r.data);
      toast.success("Brief ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="deal-closer-brief">
      <form onSubmit={run} className="border border-black/10 bg-[#F8F9FA] p-6 grid md:grid-cols-3 gap-5 items-end">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Company</span>
          <input
            className={input}
            placeholder="STC, Aramco, Noon…"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            data-testid="brief-company"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Role</span>
          <input
            className={input}
            placeholder="Senior Backend Engineer"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="brief-role"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Interviewer (optional)
          </span>
          <input
            className={input}
            placeholder="e.g. Faisal Al-Otaibi"
            value={form.interviewer}
            onChange={(e) => setForm({ ...form, interviewer: e.target.value })}
            data-testid="brief-interviewer"
          />
        </label>
        <div className="md:col-span-3">
          <button
            disabled={loading}
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-4 flex items-center gap-3 transition-colors disabled:opacity-60"
            data-testid="brief-run"
          >
            <Sparkles className="w-4 h-4" />
            {loading ? "Preparing brief…" : "Generate 48-hour brief"}
          </button>
        </div>
      </form>

      {brief && brief.raw && (
        <div className="border border-black/10 p-5 bg-white whitespace-pre-wrap font-mono text-sm">
          {brief.raw}
        </div>
      )}

      {brief && !brief.raw && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="brief-result">
          <Section title="Company snapshot">
            <BulletList items={brief.company_snapshot} />
          </Section>
          <Section title="Why you fit">
            <BulletList items={brief.role_fit} />
          </Section>
          <Section title="Likely questions">
            <BulletList items={brief.likely_questions} />
          </Section>
          <Section title="Smart questions to ask" accent="text-[#34C759]">
            <BulletList items={brief.smart_questions_to_ask} />
          </Section>
          <Section title="Red flags to watch" accent="text-[#FF3B30]">
            <BulletList items={brief.red_flags} />
          </Section>
        </div>
      )}

      {!brief && !loading && (
        <div className="border border-dashed border-black/20 p-10 text-center text-sm text-gray-500">
          Fill the form above to get a 48-hour interview prep brief powered by Claude Sonnet 4.5.
        </div>
      )}
    </div>
  );
}

function OfferNegotiate() {
  const [form, setForm] = useState({
    company: "",
    role: "",
    offered_base_sar: "",
    offered_extras: "",
    target_base_sar: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    if (!form.company || !form.role || !form.offered_base_sar || !form.target_base_sar) {
      toast.error("Company, role, offer and target are required");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        offered_base_sar: parseInt(form.offered_base_sar) || 0,
        target_base_sar: parseInt(form.target_base_sar) || 0,
      };
      const r = await api.post("/ai/offer-negotiate", payload);
      setResult(r.data);
      toast.success("Counter-offer ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = async () => {
    if (!result?.counter_email) return;
    await navigator.clipboard.writeText(result.counter_email);
    toast.success("Copied");
  };

  return (
    <div className="space-y-8" data-testid="deal-closer-negotiate">
      <form onSubmit={run} className="border border-black/10 bg-[#F8F9FA] p-6 grid md:grid-cols-2 gap-5 items-end">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Company</span>
          <input
            className={input}
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            data-testid="neg-company"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Role</span>
          <input
            className={input}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="neg-role"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Offered base salary (SAR / month)
          </span>
          <input
            type="number"
            className={input}
            value={form.offered_base_sar}
            onChange={(e) => setForm({ ...form, offered_base_sar: e.target.value })}
            data-testid="neg-offered"
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Your target base (SAR / month)
          </span>
          <input
            type="number"
            className={input}
            value={form.target_base_sar}
            onChange={(e) => setForm({ ...form, target_base_sar: e.target.value })}
            data-testid="neg-target"
          />
        </label>
        <label className="md:col-span-2 block">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
            Offered extras (housing, transport, bonus…)
          </span>
          <input
            className={input}
            placeholder="e.g. 25% housing, SAR 1000 transport, 15% bonus"
            value={form.offered_extras}
            onChange={(e) => setForm({ ...form, offered_extras: e.target.value })}
            data-testid="neg-extras"
          />
        </label>
        <div className="md:col-span-2">
          <button
            disabled={loading}
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-4 flex items-center gap-3 transition-colors disabled:opacity-60"
            data-testid="neg-run"
          >
            <Target className="w-4 h-4" />
            {loading ? "Calculating counter…" : "Draft counter-offer"}
          </button>
        </div>
      </form>

      {result && (
        <div className="space-y-6" data-testid="neg-result">
          <div className="grid sm:grid-cols-3 gap-0 border border-black/10">
            <div className="p-6 border-e border-black/10">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Market median</div>
              <div className="font-display font-black text-3xl text-[#0033FF]">
                SAR {Number(result.market_median_sar || 0).toLocaleString()}
              </div>
            </div>
            <div className="p-6 border-e border-black/10">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Their offer</div>
              <div className="font-display font-black text-3xl">
                SAR {Number(form.offered_base_sar || 0).toLocaleString()}
              </div>
            </div>
            <div className="p-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Your target</div>
              <div className="font-display font-black text-3xl text-[#34C759]">
                SAR {Number(form.target_base_sar || 0).toLocaleString()}
              </div>
            </div>
          </div>

          {result.raw && (
            <div className="border border-black/10 p-5 bg-white whitespace-pre-wrap font-mono text-sm">
              {result.raw}
            </div>
          )}

          {!result.raw && (
            <>
              <Section title="Analysis">
                <BulletList items={result.analysis} />
              </Section>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-3">
                  Negotiation levers
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {(result.levers || []).map((l, i) => (
                    <div key={i} className="border border-black/10 p-4 bg-white">
                      <div className="font-display font-bold mb-1">{l.lever}</div>
                      <div className="text-sm text-[#0033FF] mb-2">{l.ask}</div>
                      <div className="text-xs text-gray-600 leading-relaxed">{l.rationale}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-black/10 bg-white">
                <div className="p-5 border-b border-black/10 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-4 h-4 text-[#0033FF]" />
                    <div className="font-display font-bold">Counter-offer email</div>
                  </div>
                  <button
                    onClick={copyEmail}
                    className="bg-black text-white hover:bg-[#0033FF] font-bold px-4 py-2 text-sm flex items-center gap-2 transition-colors"
                    data-testid="neg-copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
                <pre
                  className="p-5 whitespace-pre-wrap font-mono text-sm leading-relaxed"
                  data-testid="neg-email"
                >
                  {result.counter_email}
                </pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function DealCloser() {
  const { t } = useI18n();
  const [tab, setTab] = useState("brief");

  return (
    <div className="space-y-8 max-w-5xl" data-testid="deal-closer-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
          / {t("deal_closer")}
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          Close the deal.
        </h1>
        <p className="text-gray-600 mt-3 max-w-2xl">
          A 48-hour interview briefing and a salary counter-offer drafted for you — measured in riyals.
        </p>
      </div>

      <div className="flex gap-0 border-b border-black/10">
        <button
          onClick={() => setTab("brief")}
          className={`px-5 py-3 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
            tab === "brief" ? "border-black text-black" : "border-transparent text-gray-500 hover:text-black"
          }`}
          data-testid="tab-brief"
        >
          <Briefcase className="w-4 h-4" />
          Interview brief
        </button>
        <button
          onClick={() => setTab("negotiate")}
          className={`px-5 py-3 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px transition-colors ${
            tab === "negotiate" ? "border-black text-black" : "border-transparent text-gray-500 hover:text-black"
          }`}
          data-testid="tab-negotiate"
        >
          <Target className="w-4 h-4" />
          Negotiate offer
        </button>
      </div>

      {tab === "brief" ? <InterviewBrief /> : <OfferNegotiate />}

      <div className="border border-black/10 bg-[#F8F9FA] p-5 text-xs text-gray-600 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-[#FF3B30] shrink-0 mt-0.5" />
        <span>
          AI guidance for general market context — always cross-check against your specific contract and
          Saudi labor regulations before committing.
        </span>
      </div>
    </div>
  );
}
